import React, { useState, useEffect } from 'react';
import { liveDataAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Globe, TrendingUp, Cloud, Activity } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminLiveData() {
  const [weather, setWeather] = useState(null);
  const [markets, setMarkets] = useState(null);
  const [sports, setSports] = useState(null);
  const [city, setCity] = useState('London');
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  const loadAll = async (c = city) => {
    setLoading(true);
    try {
      const [wRes, mRes, sRes] = await Promise.all([
        liveDataAPI.weather(c).catch(() => ({ data: null })),
        liveDataAPI.markets().catch(() => ({ data: null })),
        liveDataAPI.sports().catch(() => ({ data: null })),
      ]);
      setWeather(wRes.data);
      setMarkets(mRes.data);
      setSports(sRes.data);
    } catch { toast.error('Failed to load live data'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Globe className="w-6 h-6 text-[#B89B5E]" /> Live Data Feeds</h1>
        <p className="text-sm text-[#5B616E] mt-1">Embed real-time data in articles and dashboards</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Weather */}
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Cloud className="w-4 h-4" /> Weather</CardTitle>
              <Badge variant="outline" className="text-[10px]">{weather?.source || 'stub'}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3">
              <Input value={city} onChange={e => setCity(e.target.value)} placeholder="City" className="text-xs" />
              <Button size="sm" variant="outline" onClick={() => loadAll(city)}>Get</Button>
            </div>
            {weather && (
              <div className="text-center">
                <p className="text-3xl font-bold">{weather.temp_c}°C</p>
                <p className="text-sm text-[#5B616E] capitalize">{weather.description}</p>
                <p className="text-xs text-[#5B616E] mt-1">{weather.city} · Humidity {weather.humidity_pct}% · Wind {weather.wind_kph} km/h</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Markets */}
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Financial Markets</CardTitle>
              <Badge variant="outline" className="text-[10px]">{markets?.source?.includes('stub') ? 'stub' : 'live'}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {markets?.indices?.map(idx => (
              <div key={idx.symbol} className="flex items-center justify-between py-2 border-b border-[#E7E0D6] last:border-0">
                <div>
                  <p className="text-sm font-medium">{idx.name}</p>
                  <p className="text-xs text-[#5B616E]">{idx.symbol}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold">{idx.value?.toLocaleString()}</p>
                  <p className={`text-xs font-medium ${idx.change >= 0 ? 'text-green-600' : 'text-red-500'}`}>{idx.change >= 0 ? '+' : ''}{idx.change}%</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Sports */}
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4" /> Sports Scores</CardTitle>
              <Badge variant="outline" className="text-[10px]">{sports?.source || 'stub'}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {sports?.matches?.map((m, i) => (
              <div key={i} className="p-2 border border-[#E7E0D6] rounded-lg mb-2">
                <p className="text-xs text-[#5B616E] mb-1">{m.competition}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{m.home}</span>
                  <span className="text-lg font-bold text-[#B89B5E]">{m.score}</span>
                  <span className="text-sm font-medium">{m.away}</span>
                </div>
                <p className="text-[10px] text-center text-[#5B616E] mt-1">{m.status}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-[#FBF7EF] border-[#E7E0D6]">
        <CardContent className="pt-4">
          <p className="text-sm font-medium mb-2">Embed live data in articles</p>
          <p className="text-xs text-[#5B616E]">Use the API endpoints below to pull live data into article blocks and public pages. Configure API keys in environment variables to activate live data sources.</p>
          <div className="mt-3 space-y-1 font-mono text-xs text-[#5B616E]">
            <p>GET /api/live-data/weather?city=London</p>
            <p>GET /api/live-data/markets</p>
            <p>GET /api/live-data/sports</p>
            <p>GET /api/live-data/election-tracker</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
